import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import PageHeader from '../components/layout/PageHeader';
import { staffTabClass, staffCardClass, staffInputClass } from '../components/layout/staffPageStyles';
import { Sparkles, Calendar, RotateCw, Settings as SettingsIcon, Save } from 'lucide-react';

export default function AdminPromotionsPage() {
  const { api } = useAuth();
  const { t, formatCurrency } = useLanguage();
  const [tab, setTab] = useState('current');
  const [promos, setPromos] = useState([]);
  const [calendar, setCalendar] = useState(null);
  const [settings, setSettings] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [busy, setBusy] = useState(false);

  const loadCurrent = async () => {
    const r = await api.get('/admin/promotions');
    setPromos(r.data.items || []);
  };
  const loadCalendar = async () => {
    const r = await api.get(`/admin/promotions/calendar/${month}`);
    setCalendar(r.data);
  };
  // 2026-05-15: email campaigns disabled (user_decision_no_more_campaigns_2026-05-15)
  const loadSettings = async () => {
    const r = await api.get('/admin/promotions/settings');
    setSettings(r.data);
  };

  useEffect(() => {
    if (tab === 'current') loadCurrent();
    if (tab === 'calendar') loadCalendar();
    if (tab === 'settings') loadSettings();
  }, [tab, month]);

  const regenDaily = async () => {
    if (!window.confirm(t('adminPromos.confirmRegenDaily'))) return;
    setBusy(true);
    const r = await api.post('/admin/promotions/regenerate-daily');
    setBusy(false);
    alert(r.data?.status === 'ok'
      ? t('adminPromos.drawResult', { name: r.data.promotion?.product_designation })
      : t('adminPromos.statusResult', { status: r.data?.status }));
    loadCurrent();
  };
  const regenCalendar = async () => {
    if (!window.confirm(t('adminPromos.confirmRegenCalendar', { month }))) return;
    setBusy(true);
    const r = await api.post(`/admin/promotions/regenerate-calendar/${month}`);
    setBusy(false);
    alert(t('adminPromos.calendarRegenResult', { status: r.data?.status, count: r.data?.products_used || 0 }));
    loadCalendar();
  };
  const saveSettings = async () => {
    await api.put('/admin/promotions/settings', settings);
    alert(t('adminPromos.settingsSaved'));
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-6xl mx-auto" data-testid="admin-promotions-page">
        <PageHeader
          title={t('adminPromos.title')}
          subtitle={t('adminPromos.subtitle')}
          backTo="/dashboard"
          icon={Sparkles}
          testId="admin-promotions-header"
        />

        <nav className="flex gap-2 flex-wrap">
          {[
            ['current', t('adminPromos.tabCurrent'), Sparkles],
            ['calendar', t('adminPromos.tabCalendar'), Calendar],
            ['settings', t('adminPromos.tabSettings'), SettingsIcon],
          ].map(([k, label, Ico]) => (
            <button
              key={k}
              type="button"
              onClick={() => setTab(k)}
              className={staffTabClass(tab === k)}
              data-testid={`promo-tab-${k}`}
            >
              <Ico className="w-4 h-4" /> {label}
            </button>
          ))}
        </nav>

        {tab === 'current' && (
          <section className="space-y-4">
            <div className="flex gap-2">
              <button onClick={regenDaily} disabled={busy}
                className="flex items-center gap-2 bg-rose-600 px-4 py-2 rounded text-white font-semibold disabled:opacity-50"
                data-testid="regen-daily">
                <RotateCw className="w-4 h-4" /> {t('adminPromos.forceDailyDraw')}
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {promos.map(p => (
                <div key={p.id} className="bg-slate-900/60 border border-slate-700 rounded-xl p-4">
                  <div className="text-xs uppercase text-amber-400">{p.type}</div>
                  <div className="text-lg font-semibold mt-1">{p.product_designation}</div>
                  <div className="text-sm text-slate-400">{t('adminPromos.ref', { ref: p.product_ref })}</div>
                  <div className="text-3xl font-bold mt-2 text-rose-400">−{p.discount_percent}%</div>
                  <div className="text-sm">
                    <span className="line-through text-slate-500">{formatCurrency(Number(p.product_price_original))}</span>
                    {' → '}
                    <span className="text-amber-400 font-bold">{formatCurrency(Number(p.price_after))}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-2">
                    {p.active ? t('adminPromos.statusActive') : t('adminPromos.statusInactive')} — {t('adminPromos.endLabel', { date: (p.end_at || '').slice(0, 16).replace('T', ' ') })}
                  </div>
                </div>
              ))}
              {!promos.length && <div className="col-span-full text-center text-slate-500 py-6">{t('adminPromos.emptyPromos')}</div>}
            </div>
          </section>
        )}

        {tab === 'calendar' && (
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <input type="month" value={month} onChange={e => setMonth(e.target.value)}
                className="bg-slate-800 border border-slate-700 rounded px-3 py-1" />
              <button onClick={regenCalendar} disabled={busy}
                className="flex items-center gap-2 bg-rose-600 px-4 py-2 rounded text-white font-semibold">
                <RotateCw className="w-4 h-4" /> {t('adminPromos.regenThisMonth')}
              </button>
            </div>
            <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {(calendar?.days || []).map(d => (
                  <div key={d.date} className="bg-slate-800/50 rounded p-3">
                    <div className="font-semibold text-amber-400">{d.date}</div>
                    <div className="text-xs text-slate-400 mb-2">{t('adminPromos.productsCount', { count: d.products?.length || 0 })}</div>
                    <ul className="text-sm space-y-1">
                      {(d.products || []).slice(0, 5).map(p => (
                        <li key={p.product_id} className="flex justify-between">
                          <span className="truncate mr-2">{p.product_designation}</span>
                          <span className="text-rose-400">−{p.discount_percent}%</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                {!(calendar?.days?.length) && <div className="col-span-full text-center text-slate-500 py-6">{t('adminPromos.emptyCalendar')}</div>}
              </div>
            </div>
          </section>
        )}

        {tab === 'settings' && settings && (
          <section className="bg-slate-900/60 border border-slate-700 rounded-xl p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <PromoConfig title={t('adminPromos.configDaily')} enabled={settings.daily_enabled}
                onEnabled={v => setSettings({ ...settings, daily_enabled: v })}
                min={settings.daily_min_pct} max={settings.daily_max_pct}
                onMin={v => setSettings({ ...settings, daily_min_pct: v })}
                onMax={v => setSettings({ ...settings, daily_max_pct: v })} />

              <PromoConfig title={t('adminPromos.tabCalendar')} enabled={settings.calendar_enabled}
                onEnabled={v => setSettings({ ...settings, calendar_enabled: v })}
                min={settings.calendar_min_pct} max={settings.calendar_max_pct}
                onMin={v => setSettings({ ...settings, calendar_min_pct: v })}
                onMax={v => setSettings({ ...settings, calendar_max_pct: v })} />
            </div>

            <div>
              <label className="text-sm text-slate-400">{t('adminPromos.excludedCategories')}</label>
              <input type="text" className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 mt-1"
                value={(settings.excluded_categories || []).join(', ')}
                onChange={e => setSettings({ ...settings, excluded_categories: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} />
            </div>

            <div>
              <label className="text-sm text-slate-400">{t('adminPromos.excludedKeywords')}</label>
              <input type="text" className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 mt-1"
                value={(settings.excluded_keywords || []).join(', ')}
                onChange={e => setSettings({ ...settings, excluded_keywords: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} />
            </div>

            <button onClick={saveSettings}
              className="flex items-center gap-2 bg-amber-500 text-black font-semibold px-4 py-2 rounded">
              <Save className="w-4 h-4" /> {t('adminPromos.save')}
            </button>
          </section>
        )}
      </div>
    </Layout>
  );
}

function PromoConfig({ title, enabled, onEnabled, min, max, onMin, onMax }) {
  const { t } = useLanguage();
  return (
    <div className="bg-slate-800/50 rounded p-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">{title}</h3>
        <input type="checkbox" checked={enabled || false} onChange={e => onEnabled(e.target.checked)} />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <label className="text-xs text-slate-400">{t('adminPromos.minPct')}
          <input type="number" min="0" max="50" value={min || 0} onChange={e => onMin(parseFloat(e.target.value) || 0)}
            className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1 mt-1" />
        </label>
        <label className="text-xs text-slate-400">{t('adminPromos.maxPct')}
          <input type="number" min="0" max="50" value={max || 0} onChange={e => onMax(parseFloat(e.target.value) || 0)}
            className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1 mt-1" />
        </label>
      </div>
    </div>
  );
}
